import React from 'react';
import { Outlet } from 'react-router';

export interface DashboardLayoutProps {
  /** The configured `<Sidebar />` element supplied by the app. */
  sidebar: React.ReactNode;
  /** Mobile drawer open state. The layout uses it for the overlay only. */
  isOpen: boolean;
  onToggle: () => void;
  /**
   * Optional content to render instead of the default `<Outlet />`. Mostly
   * useful for tests / Storybook; production routing should use `<Outlet />`
   * via nested routes.
   */
  children?: React.ReactNode;
}

/**
 * App-shell layout: persistent sidebar on lg+, slide-in drawer + overlay on
 * mobile, scrollable main area that hosts route content via `<Outlet />`.
 *
 * Decoupled from any state library — the consuming app (Tms uses Zustand)
 * owns the open/close state and passes it in via `isOpen` / `onToggle`.
 */
export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  sidebar,
  isOpen,
  onToggle,
  children,
}) => {
  return (
    <div className="flex h-screen bg-gray-50">
      {sidebar}

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden lg:ml-0">
        {/* Mobile Header */}
        <div className="border-b border-gray-200 bg-white px-4 py-3 lg:hidden">
          <button
            type="button"
            onClick={onToggle}
            className="rounded-md p-2 text-gray-600 hover:bg-gray-100"
            aria-label="Open sidebar"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">{children ?? <Outlet />}</main>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onToggle}
        />
      )}
    </div>
  );
};
